/**
 * PresetsModal handlers
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import {
  usePresets,
  useCreatePreset,
  useUpdatePreset,
  useDeletePreset,
  useApplyPreset,
  useCountryCreative,
  useReorderPresets,
  useReorderPresetsInFolder,
  bulkSendPreparedDropsStream,
} from '@/hooks';
import { useListTagRules } from '@/hooks/smswiser/lists';
import { useToast } from '@/providers/ToastProvider';
import { formatSendErrorDescription, getApiErrorMessage } from '@/lib/utils';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { getDefaultCampaign } from '@/features/smswiser-campaigns/utils/defaultCampaignStorage';

import { coalescePresetOrderByGroup, mergeFilteredReorder } from '../list/sortable';

import type { Preset } from '../shared';
import { preparedDropToPresetItemPayload, presetItemToPayload } from '../shared';
import { ruleBasedPresetNeedsApplyRouting } from '../shared/preset-bulk-apply-routing.utils';

export function usePresetsModal(
  countryKey: string,
  workDate: string,
  campaignWiserId: number,
  onApplied?: () => void,
  onClose?: () => void,
  _selectedDraftsCount?: number,
  campaigns: Array<{ id: number; name: string }> = [],
) {
  const { success: toastSuccess, error: toastError, loading: toastLoading, update: toastUpdate, dismiss: toastDismiss } = useToast();
  const presetsQuery = usePresets(countryKey);
  const creativeQuery = useCountryCreative(countryKey);
  const createPreset = useCreatePreset(countryKey);
  const updatePreset = useUpdatePreset(countryKey);
  const deletePreset = useDeletePreset(countryKey);
  const applyPreset = useApplyPreset();
  const reorderPresetsMutation = useReorderPresets(countryKey);
  const reorderPresetsInFolderMutation = useReorderPresetsInFolder(countryKey);

  const [applyingPreset, setApplyingPreset] = useState<Preset | null>(null);
  const [editingPreset, setEditingPreset] = useState<Preset | null>(null);
  const [applyWorkDate, setApplyWorkDate] = useState(workDate);
  const [applyCampaignWiserId, setApplyCampaignWiserId] = useState(campaignWiserId);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateRuleBased, setShowCreateRuleBased] = useState(false);

  const tagRulesQuery = useListTagRules();
  const availableTags = useMemo(() => {
    const tags = new Set<string>(['New', 'Unused']);
    for (const rule of tagRulesQuery.data ?? []) {
      if (rule.enabled && rule.name?.trim()) tags.add(rule.name.trim());
    }
    return [...tags].sort();
  }, [tagRulesQuery.data]);
  const tagColors = useMemo(() => {
    const colors: Record<string, string> = {};
    for (const rule of tagRulesQuery.data ?? []) {
      if (rule.enabled && rule.name?.trim() && rule.color) {
        colors[rule.name.trim()] = rule.color;
      }
    }
    return colors;
  }, [tagRulesQuery.data]);

  const presets = presetsQuery.data ?? [];
  const availableGroupNames = useMemo(() => {
    const set = new Set<string>();
    for (const p of presets) {
      const g = p.groupName?.trim();
      if (g) set.add(g);
    }
    return [...set].sort();
  }, [presets]);

  const isBusy =
    createPreset.isPending ||
    updatePreset.isPending ||
    deletePreset.isPending ||
    applyPreset.isPending ||
    reorderPresetsMutation.isPending ||
    reorderPresetsInFolderMutation.isPending;

  const handleApply = useCallback(
    (preset: Preset) => {
      setApplyingPreset(preset);
      setApplyWorkDate(workDate);
      const defaultId = getDefaultCampaign(countryKey);
      const valid = defaultId != null && campaigns.some((c) => c.id === defaultId);
      setApplyCampaignWiserId(valid ? defaultId : 0);
    },
    [workDate, countryKey, campaigns],
  );

  const handleApplyConfirm = useCallback(
    async (
      wDate: string,
      wCampaignWiserId: number,
      opts: {
        smsTextsOverride?: string[];
        smsTextsPerDrop?: string[][];
        senderNamesOverride?: string[];
        senderNamesPerDrop?: string[][];
        routeId?: number;
        shortLinkTldValue?: string;
        andSend?: boolean;
      },
    ) => {
      if (!applyingPreset) return;
      const itemCampaignWiserIdOverrides = Object.fromEntries(
        applyingPreset.items
          .filter((i) => i.campaignWiserId != null && Number.isFinite(i.campaignWiserId))
          .map((i) => [i.id, i.campaignWiserId as number]),
      );
      const {
        smsTextsOverride,
        smsTextsPerDrop,
        senderNamesOverride,
        senderNamesPerDrop,
        routeId,
        shortLinkTldValue,
        andSend,
      } = opts;
      try {
        const res = await applyPreset.mutateAsync({
          countryKey,
          presetId: applyingPreset.id,
          workDate: wDate,
          campaignWiserId: wCampaignWiserId,
          itemCampaignWiserIdOverrides:
            Object.keys(itemCampaignWiserIdOverrides).length > 0 ? itemCampaignWiserIdOverrides : undefined,
          smsTextsOverride,
          smsTextsPerDrop,
          senderNamesOverride,
          senderNamesPerDrop,
          routeId,
          shortLinkTldValue,
        });
        setApplyingPreset(null);
        if (andSend && res.createdIds?.length > 0 && res.wiserIds?.length === res.createdIds.length) {
          const byWiserId = new Map<number, string[]>();
          for (let i = 0; i < res.createdIds.length; i++) {
            const wid = res.wiserIds[i]!;
            const arr = byWiserId.get(wid) ?? [];
            arr.push(res.createdIds[i]!);
            byWiserId.set(wid, arr);
          }
          const total = res.createdIds.length;
          const loadingId = toastLoading('Sending drops...', `0 of ${total} sent`);
          let sent = 0;
          let failed = 0;
          const errorMessages: string[] = [];
          try {
            const { getAuthToken, getApiBaseUrl } = await import('@/services/api');
            for (const [wid, ids] of byWiserId) {
              await bulkSendPreparedDropsStream(countryKey, wid, ids, {
                getAuthToken,
                getApiBase: getApiBaseUrl,
                onEvent: (ev) => {
                  if (ev.status === 'sent') {
                    sent++;
                    toastUpdate(loadingId, 'Sending drops...', `${sent} of ${total} sent`);
                  }
                  if (ev.status === 'error') {
                    failed++;
                    if (ev.message) errorMessages.push(ev.message);
                  }
                },
              });
            }
            toastDismiss(loadingId);
            if (failed > 0) {
              toastError(
                'Bulk send finished with errors',
                formatSendErrorDescription({ sent, failed, errorMessages }),
              );
            } else {
              toastSuccess('Created and sent', `${res.created} drop${res.created !== 1 ? 's' : ''} created and sent for ${formatDateDMY(wDate)}.`);
            }
          } catch (sendErr) {
            toastDismiss(loadingId);
            toastError('Bulk send failed', getApiErrorMessage(sendErr, 'Could not send drops'));
          }
        } else {
          toastSuccess('Preset applied', `${res.created} drop${res.created !== 1 ? 's' : ''} created for ${formatDateDMY(wDate)}.`);
        }
        onApplied?.();
        onClose?.();
      } catch (err) {
        toastError('Apply failed', getApiErrorMessage(err, 'Could not create drops'));
      }
    },
    [
      applyingPreset,
      applyPreset,
      countryKey,
      toastSuccess,
      toastError,
      toastLoading,
      toastUpdate,
      toastDismiss,
      onApplied,
      onClose,
    ],
  );

  const handleEdit = useCallback((preset: Preset) => setEditingPreset(preset), []);

  const handleEditSave = useCallback(
    async (
      payload:
        | {
            id: string;
            name: string;
            groupName?: string;
            folderIds?: string[];
            items: import('../shared').CreatePresetPayload['items'];
          }
        | {
            id: string;
            name: string;
            groupName?: string;
            folderIds?: string[];
            presetType: 'rule-based';
            isPublic?: boolean;
            listRule: Record<string, unknown>;
            template: import('../shared').PresetTemplatePayload;
          },
    ) => {
      try {
        if ('presetType' in payload && payload.presetType === 'rule-based') {
          await updatePreset.mutateAsync({
            id: payload.id,
            name: payload.name,
            groupName: payload.groupName,
            presetType: 'rule-based',
            isPublic: payload.isPublic,
            listRule: payload.listRule,
            template: payload.template,
            folderIds: payload.folderIds,
          });
        } else {
          await updatePreset.mutateAsync({
            id: payload.id,
            name: payload.name,
            groupName: payload.groupName,
            items: (payload as { items: import('../shared').CreatePresetPayload['items'] }).items,
            folderIds: (payload as { folderIds?: string[] }).folderIds,
          });
        }
        toastSuccess('Preset updated', `"${payload.name}" saved.`);
        setEditingPreset(null);
      } catch (err) {
        toastError('Update failed', getApiErrorMessage(err, 'Could not update preset'));
      }
    },
    [updatePreset, toastSuccess, toastError],
  );

  const handlePresetsReorder = useCallback(
    (orderedIds: string[], visiblePresets?: Preset[], activeFolderId?: string | null) => {
      if (activeFolderId != null) {
        reorderPresetsInFolderMutation.mutate({
          folderId: activeFolderId,
          orderedPresetIds: orderedIds,
        });
        return;
      }
      const fullIds = presets.map((p) => p.id);
      let next = orderedIds;
      if (visiblePresets != null && visiblePresets.length < presets.length) {
        const filteredSet = new Set(visiblePresets.map((p) => p.id));
        next = mergeFilteredReorder(fullIds, orderedIds, filteredSet);
      }
      next = coalescePresetOrderByGroup(next, presets);
      reorderPresetsMutation.mutate(next);
    },
    [presets, reorderPresetsMutation, reorderPresetsInFolderMutation],
  );

  const handleBulkApplyPresets = useCallback(
    async (
      presetList: Preset[],
      wDate: string,
      wCampaignWiserId: number,
      matchListOrder: boolean,
      bulkRouting?: { routeId: number; shortLinkTldValue: string },
    ) => {
      if (presetList.length === 0) return;
      if (wCampaignWiserId < 1) {
        toastError('Campaign required', 'Choose a campaign in the bulk bar before applying presets.');
        return;
      }
      const ordered = matchListOrder ? [...presetList].reverse() : [...presetList];
      for (const p of ordered) {
        if (!ruleBasedPresetNeedsApplyRouting(p)) continue;
        const rid = bulkRouting?.routeId ?? 0;
        const tld = bulkRouting?.shortLinkTldValue?.trim() ?? '';
        if (rid < 1 || !tld) {
          toastError(
            'Gateway and TLD required',
            'Rule-based presets in your selection need a gateway and short-link TLD. Set them in the bulk bar, or add them to each preset template.',
          );
          return;
        }
      }
      const n = ordered.length;
      const loadingId = toastLoading('Applying presets', `0 / ${n}`);
      let total = 0;
      try {
        let done = 0;
        for (const p of ordered) {
          const needsRouting = ruleBasedPresetNeedsApplyRouting(p);
          const itemCampaignWiserIdOverrides = Object.fromEntries(
            p.items
              .filter((i) => i.campaignWiserId != null && Number.isFinite(i.campaignWiserId))
              .map((i) => [i.id, i.campaignWiserId as number]),
          );
          const res = await applyPreset.mutateAsync({
            countryKey,
            presetId: p.id,
            workDate: wDate,
            campaignWiserId: wCampaignWiserId,
            itemCampaignWiserIdOverrides:
              Object.keys(itemCampaignWiserIdOverrides).length > 0
                ? itemCampaignWiserIdOverrides
                : undefined,
            routeId: needsRouting ? bulkRouting?.routeId : undefined,
            shortLinkTldValue: needsRouting ? bulkRouting?.shortLinkTldValue.trim() : undefined,
          });
          total += res.created;
          done += 1;
          toastUpdate(loadingId, 'Applying presets', `${done} / ${n}`);
        }
        toastDismiss(loadingId);
        toastSuccess('Presets applied', `${total} drop${total !== 1 ? 's' : ''} created for ${formatDateDMY(wDate)}.`);
        onApplied?.();
        onClose?.();
      } catch (err) {
        toastDismiss(loadingId);
        toastError('Bulk apply failed', getApiErrorMessage(err, 'Could not apply presets'));
      }
    },
    [applyPreset, countryKey, toastSuccess, toastError, toastLoading, toastUpdate, toastDismiss, onApplied, onClose],
  );

  const handleDelete = useCallback(
    async (preset: Preset) => {
      if (!confirm(`Delete preset "${preset.name}"?`)) return;
      try {
        await deletePreset.mutateAsync(preset.id);
        toastSuccess('Preset deleted', `"${preset.name}" removed.`);
      } catch (err) {
        toastError('Delete failed', getApiErrorMessage(err, 'Could not delete preset'));
      }
    },
    [deletePreset, toastSuccess, toastError],
  );

  const handleCreate = useCallback(
    async (payload: Parameters<typeof createPreset.mutateAsync>[0]) => {
      try {
        await createPreset.mutateAsync(payload);
        toastSuccess('Preset created', `"${payload.name}" saved.`);
        setShowCreate(false);
      } catch (err) {
        toastError('Create failed', getApiErrorMessage(err, 'Could not create preset'));
      }
    },
    [createPreset, toastSuccess, toastError],
  );

  const handleAddToPreset = useCallback(
    async (preset: Preset, selectedDrops: PreparedDrop[]) => {
      if (preset.presetType === 'rule-based' || selectedDrops.length === 0) return;
      const existingPayloads = preset.items.map(presetItemToPayload);
      const newPayloads = selectedDrops.map(preparedDropToPresetItemPayload);
      const items = [...existingPayloads, ...newPayloads];
      try {
        await updatePreset.mutateAsync({
          id: preset.id,
          name: preset.name,
          groupName: preset.groupName ?? undefined,
          items,
        });
        toastSuccess('Preset updated', `Added ${selectedDrops.length} drop${selectedDrops.length !== 1 ? 's' : ''} to "${preset.name}".`);
      } catch (err) {
        toastError('Update failed', getApiErrorMessage(err, 'Could not add drops to preset'));
      }
    },
    [updatePreset, toastSuccess, toastError],
  );

  return {
    presets,
    creative: creativeQuery.data,
    presetsQuery,
    createPreset,
    updatePreset,
    deletePreset,
    applyPreset,
    applyingPreset,
    setApplyingPreset,
    editingPreset,
    setEditingPreset,
    applyWorkDate,
    setApplyWorkDate,
    applyCampaignWiserId,
    setApplyCampaignWiserId,
    showCreate,
    setShowCreate,
    showCreateRuleBased,
    setShowCreateRuleBased,
    availableTags,
    availableGroupNames,
    tagColors,
    isBusy,
    handleApply,
    handleApplyConfirm,
    handleEdit,
    handleEditSave,
    handleDelete,
    handleCreate,
    handleAddToPreset,
    handlePresetsReorder,
    handleBulkApplyPresets,
  };
}
